import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore } from '@/stores/auth';

// Only HomePage is eagerly loaded (landing page).
// All other routes use dynamic imports for code splitting.
import HomePage from '@/views/HomePage.vue';

const routes = [
    // Public routes
    {
        path: '/',
        name: 'home',
        component: HomePage,
        meta: { title: 'Home' }
    },
    {
        path: '/produkte',
        name: 'products',
        component: () => import('@/views/ProductsPage.vue'),
        meta: { title: 'Produkte' }
    },
    {
        path: '/unternehmen',
        name: 'about',
        component: () => import('@/views/AboutPage.vue'),
        meta: { title: 'Unternehmen' }
    },
    {
        path: '/karriere',
        name: 'careers',
        component: () => import('@/views/CareersPage.vue'),
        meta: { title: 'Karriere' }
    },
    {
        path: '/aktuelles',
        name: 'news',
        component: () => import('@/views/NewsPage.vue'),
        meta: { title: 'Aktuelles' }
    },
    {
        path: '/aktuelles/:slug',
        name: 'news-detail',
        component: () => import('@/views/NewsDetailPage.vue'),
        meta: { title: 'Artikel' }
    },
    {
        path: '/kontakt',
        name: 'contact',
        component: () => import('@/views/ContactPage.vue'),
        meta: { title: 'Kontakt' }
    },
    {
        path: '/impressum',
        name: 'impressum',
        component: () => import('@/views/ImpressumPage.vue'),
        meta: { title: 'Impressum' }
    },
    {
        path: '/datenschutz',
        name: 'datenschutz',
        component: () => import('@/views/DatenschutzPage.vue'),
        meta: { title: 'Datenschutz' }
    },

    // Admin routes — lazy loaded, never shipped to public visitors
    {
        path: '/admin/login',
        name: 'admin-login',
        component: () => import('@/views/admin/AdminLogin.vue'),
        meta: { title: 'Admin Login', requiresGuest: true }
    },
    {
        path: '/admin',
        name: 'admin-dashboard',
        component: () => import('@/views/admin/AdminDashboard.vue'),
        meta: { title: 'Dashboard', requiresAuth: true }
    },
    {
        path: '/admin/blog',
        name: 'admin-blog',
        component: () => import('@/views/admin/BlogEditor.vue'),
        meta: { title: 'Blog verwalten', requiresAuth: true }
    },
    {
        path: '/admin/blog/:id',
        name: 'admin-blog-edit',
        component: () => import('@/views/admin/BlogEditor.vue'),
        meta: { title: 'Artikel bearbeiten', requiresAuth: true }
    },
    {
        path: '/admin/jobs',
        name: 'admin-jobs',
        component: () => import('@/views/admin/JobEditor.vue'),
        meta: { title: 'Stellenanzeigen verwalten', requiresAuth: true }
    },
    {
        path: '/admin/jobs/:id',
        name: 'admin-job-edit',
        component: () => import('@/views/admin/JobEditor.vue'),
        meta: { title: 'Stellenanzeige bearbeiten', requiresAuth: true }
    },
    {
        path: '/admin/users',
        name: 'admin-users',
        component: () => import('@/views/admin/UserManagement.vue'),
        meta: { title: 'Benutzer verwalten', requiresAuth: true, requiresAdmin: true }
    },

    // Catch-all 404
    {
        path: '/:pathMatch(.*)*',
        name: 'not-found',
        component: () => import('@/views/NotFoundPage.vue'),
        meta: { title: 'Seite nicht gefunden' }
    }
];

const router = createRouter({
    history: createWebHistory(),
    routes,
    scrollBehavior(to, from, savedPosition) {
        if (savedPosition) {
            return savedPosition;
        }
        if (to.hash) {
            return { el: to.hash };
        }
        return { top: 0 };
    }
});

// Navigation guards
router.beforeEach(async (to, from, next) => {
    const authStore = useAuthStore();

    // Update page title
    document.title = `${to.meta.title || 'M&R Filtermedien'} | M&R Filtermedien`;

    // Check if route requires authentication
    if (to.meta.requiresAuth) {
        if (!authStore.isAuthenticated) {
            // Try to verify existing token
            await authStore.verifyToken();
        }

        if (!authStore.isAuthenticated) {
            next({ name: 'admin-login', query: { redirect: to.fullPath } });
            return;
        }

        // Admin-only routes: editors are redirected to the dashboard
        if (to.meta.requiresAdmin && !authStore.isAdmin) {
            next({ name: 'admin-dashboard' });
            return;
        }
    }

    // Check if route requires guest (login page)
    if (to.meta.requiresGuest && authStore.isAuthenticated) {
        next({ name: 'admin-dashboard' });
        return;
    }

    next();
});

// Pageview tracking — sends a lightweight beacon to the analytics API on every route change.
// Mirrors what Nginx/Caddy already log: path + referrer + IP (server-side resolution).
// No cookies, no client-side fingerprinting.
router.afterEach((to, from) => {
    try {
        const payload = JSON.stringify({
            path: to.fullPath,
            referrer: from?.fullPath || document.referrer || ''
        });
        // Fire-and-forget — don't block navigation
        if (navigator.sendBeacon) {
            navigator.sendBeacon('/api/analytics/pageview', new Blob([payload], { type: 'application/json' }));
        } else {
            // Fallback for older browsers
            fetch('/api/analytics/pageview', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: payload,
                keepalive: true
            }).catch(() => { /* ignore */ });
        }
    } catch {
        // Silently ignore tracking errors
    }
});

export default router;
